import React, { useState, useMemo, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  Link as MuiLink,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
} from '@material-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import basePCUrl from '../../../Globals';
import { fetchApiData, fetchApiDataWithTargetUserAndClientId } from '../../../utils/service';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import MyTrainingReport from './MyTrainingReport';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: theme.spacing(4),
  },
  breadcrumbBar: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 999,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.92)',
    boxShadow:
      theme.palette.type === 'dark'
        ? '0 1px 4px rgba(0, 0, 0, 0.35)'
        : '0 1px 4px rgba(15, 23, 42, 0.08)',
    border:
      theme.palette.type === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.12)'
        : '1px solid rgba(15, 23, 42, 0.06)',
  },
  breadcrumbs: {
    maxWidth: '100%',
    '& ol': {
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    '& li': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  breadcrumbSeparator: {
    color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.45)' : '#94a3b8',
    margin: theme.spacing(0, 0.25),
    fontSize: 18,
  },
  breadcrumbHome: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0055A2',
    border: 'none',
    background: 'none',
    padding: 2,
    cursor: 'pointer',
    borderRadius: 8,
    lineHeight: 1,
    '&:hover': {
      background:
        theme.palette.type === 'dark'
          ? 'rgba(66, 139, 201, 0.18)'
          : 'rgba(0, 85, 162, 0.08)',
    },
  },
  breadcrumbLink: {
    color: theme.palette.type === 'dark' ? theme.palette.text.secondary : '#475569',
    cursor: 'pointer',
    fontWeight: 500,
    border: 'none',
    background: 'none',
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 6,
    font: 'inherit',
    fontSize: '0.875rem',
    lineHeight: 1.4,
    '&:hover': {
      color: '#0055A2',
      background:
        theme.palette.type === 'dark'
          ? 'rgba(66, 139, 201, 0.18)'
          : 'rgba(0, 85, 162, 0.08)',
      textDecoration: 'none',
    },
  },
  breadcrumbUserCurrent: {
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#334155',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: theme.spacing(0.25, 0.5),
  },
  breadcrumbPageCurrent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#0055A2',
    fontWeight: 700,
    fontSize: '0.875rem',
    padding: theme.spacing(0.25, 0.5),
  },
  breadcrumbPageIcon: {
    fontSize: 18,
    color: '#0055A2',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#0f172a',
    marginBottom: theme.spacing(1),
    textAlign: 'left',
    alignSelf: 'stretch',
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  kpiGrid: {
    marginBottom: theme.spacing(4),
  },
  kpiCard: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.25)',
    },
    height: '100%',
  },
  kpiValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  successColor: { color: '#4CAF50' },
  infoColor: { color: '#2196F3' },
  warningColor: { color: '#FF9800' },
  dangerColor: { color: '#F44336' },
  riskAlert: {
    background: 'rgba(244, 67, 54, 0.1)',
    border: '1px solid #F44336',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  riskItem: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  tableContainer: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    background: 'linear-gradient(135deg, #061739 0%, #0055A2 50%, #428BC9 100%)',
    color: 'white',
    fontWeight: 600,
  },
  progressBar: {
    marginRight: theme.spacing(1),
    minWidth: '150px',
  },
  statusChip: {
    fontWeight: 600,
  },
  expandRow: {
    background: 'rgba(0, 85, 162, 0.05)',
  },
  detailsSection: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  teamCard: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0, 85, 162, 0.15)',
    },
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#0055A2',
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  exportButton: {
    background: 'linear-gradient(45deg, #0055A2, #428BC9)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #061739, #0055A2)',
    },
  },
}));

// Safe defaults for handling empty/partial API data
const EMPTY_METRICS = { totalAssigned: 0, totalCompleted: 0, completionRate: 0, inProgress: 0, notStarted: 0, overdue: 0, atRisk: false, riskScore: 0 };
const EMPTY_SUMMARY = { totalManagers: 0, totalDirectReports: 0, totalTeams: 0, totalUsers: 0, averageCompletionRate: 0, complianceStatus: 'N/A', overallRiskLevel: 'N/A' };

const isCompletedTrainingStatus = (status) =>
  typeof status === 'string' && status.trim().toLowerCase() === 'completed';

const isTrainingModuleComplete = (module) => {
  if (!module) return false;
  if (isCompletedTrainingStatus(module.status)) return true;
  const completion = Number(module.completionPercentage);
  return Number.isFinite(completion) && completion >= 100;
};

const getTrainingModuleId = (module) =>
  module?.trainingId ?? module?.training_id ?? module?.id ?? module?.moduleId ?? null;

const getTrainingModuleName = (module) =>
  module?.moduleName ?? module?.module_name ?? module?.title ?? module?.name ?? 'Training module';

const getTrainingModuleDueDate = (module) =>
  module?.dueDate ?? module?.due_date ?? module?.due ?? null;

const formatTeamReportDate = (value) => {
  if (value == null || value === '') return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getOutstandingModulesForDirectReport = (directReport) => {
  const outstanding = [];
  const seen = new Set();

  (directReport?.teams ?? []).forEach((team) => {
    (team?.members ?? []).forEach((member) => {
      (member?.assignedTraining ?? []).forEach((module) => {
        if (isTrainingModuleComplete(module)) return;

        const trainingId = getTrainingModuleId(module);
        const moduleName = getTrainingModuleName(module);
        const memberKey = member?.userId || member?.userEmail || member?.userName || 'member';
        const dedupeKey = `${memberKey}::${trainingId || moduleName}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        outstanding.push({
          key: dedupeKey,
          trainingId,
          moduleName,
          status: module?.status || 'Not Started',
          dueDate: getTrainingModuleDueDate(module),
          completionPercentage: Number(module?.completionPercentage) || 0,
          memberName: member?.userName || member?.userEmail || '—',
          memberEmail: member?.userEmail || '',
        });
      });
    });
  });

  // Prefer overdue / not started first so managers see the urgent work.
  return outstanding.sort((a, b) => {
    const statusRank = (status) => {
      const normalized = String(status || '').toLowerCase();
      if (normalized.includes('overdue')) return 0;
      if (normalized.includes('not started')) return 1;
      if (normalized.includes('progress')) return 2;
      return 3;
    };
    const byStatus = statusRank(a.status) - statusRank(b.status);
    if (byStatus !== 0) return byStatus;
    return String(a.moduleName).localeCompare(String(b.moduleName));
  });
};

const getModuleStatusChipStyle = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('overdue')) return { background: '#F44336', color: 'white' };
  if (normalized.includes('progress')) return { background: '#2196F3', color: 'white' };
  return { background: '#FF9800', color: 'white' };
};

// Compliance tiers from outstanding module count:
// OK = 0, Needs Work = 1-3, At Risk = 4+
const getOutstandingModuleCount = (directReport) => {
  const listed = getOutstandingModulesForDirectReport(directReport);
  if (listed.length > 0) return listed.length;

  const metrics = directReport?.metrics ?? EMPTY_METRICS;
  const assigned = Number(metrics.totalAssigned || 0);
  const completed = Number(metrics.totalCompleted || 0);
  if (assigned > 0 && assigned >= completed) {
    return Math.max(0, assigned - completed);
  }

  return Math.max(
    0,
    Number(metrics.overdue || 0) +
      Number(metrics.notStarted || 0) +
      Number(metrics.inProgress || 0)
  );
};

const getComplianceTier = (outstandingCount) => {
  const count = Number(outstandingCount) || 0;
  if (count >= 4) {
    return {
      level: 'at_risk',
      label: 'At Risk',
      color: '#F44336',
      outstandingCount: count,
    };
  }
  if (count >= 1) {
    return {
      level: 'needs_work',
      label: 'Needs Work',
      color: '#FF9800',
      outstandingCount: count,
    };
  }
  return {
    level: 'ok',
    label: 'OK',
    color: '#4CAF50',
    outstandingCount: 0,
  };
};

const renderComplianceStatusChip = (tier, t = (value) => value, classes) => {
  if (tier.level === 'at_risk') {
    return (
      <Chip
        icon={<InfoIcon />}
        label={`⚠️ ${t(tier.label)}`}
        size="small"
        className={classes.statusChip}
        style={{ background: tier.color, color: 'white' }}
      />
    );
  }
  if (tier.level === 'needs_work') {
    return (
      <Chip
        label={`⚠ ${t(tier.label)}`}
        size="small"
        className={classes.statusChip}
        style={{ background: tier.color, color: 'white' }}
      />
    );
  }
  return (
    <Chip
      label={`✓ ${t(tier.label)}`}
      size="small"
      className={classes.statusChip}
      style={{ background: tier.color, color: 'white' }}
    />
  );
};

// Component for KPI Card
const KPICard = ({ title, value, subtitle, trend, colorClass, classes }) => (
  <Card className={classes.kpiCard}>
    <CardContent>
      <Typography color="textSecondary" gutterBottom variant="body2">
        {title}
      </Typography>
      <Typography className={`${classes.kpiValue} ${colorClass}`} variant="h4">
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subtitle}
      </Typography>
      {trend && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
          {trend}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const resolveDirectReportTargetUser = (directReport) => {
  const name = String(directReport?.reportName || '').trim();
  // Prefer username-like names (e.g. dukandar.r); display names fall back to email local-part.
  if (name && !/\s/.test(name)) return name;
  const email = String(directReport?.reportEmail || '').trim();
  if (email.includes('@')) return email.split('@')[0];
  return name || null;
};

const formatTrailLabel = (username, fallbackLabel) => {
  const fallback = typeof fallbackLabel === 'string' ? fallbackLabel.trim() : '';
  if (fallback) return fallback;
  const raw = typeof username === 'string' ? username.trim() : '';
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const directReportHasTeam = (directReport) => {
  const teams = directReport?.teams ?? [];
  return (
    Number(directReport?.teamCount) > 0 ||
    teams.length > 0 ||
    Number(directReport?.userCount) > 0
  );
};

// Component for Direct Report Row
const DirectReportRow = ({
  directReport,
  classes,
  onExpand,
  expanded,
  t = (value) => value,
  accessLevel,
  selectedClient,
  getTranslation,
  preferredLanguage,
  onOpenAsTeamRoot,
}) => {
  const metrics = directReport?.metrics ?? EMPTY_METRICS;
  const completionRate = metrics.completionRate ?? 0;
  const complianceTier = getComplianceTier(getOutstandingModuleCount(directReport));
  const completionColor =
    complianceTier.level === 'ok'
      ? classes.successColor
      : complianceTier.level === 'needs_work'
      ? classes.warningColor
      : classes.dangerColor;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => onExpand(expanded ? null : directReport.reportId)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={{ fontWeight: 600 }}>
          {directReport.reportName}
        </TableCell>
        <TableCell align="center">{directReport.reportEmail}</TableCell>
        <TableCell align="center">{directReport.teamCount}</TableCell>
        <TableCell align="center">{directReport.userCount}</TableCell>
        <TableCell align="center">
          <Box display="flex" alignItems="center" justifyContent="center">
            <LinearProgress
              variant="determinate"
              value={completionRate}
              className={classes.progressBar}
            />
            <span className={completionColor}>
              {completionRate.toFixed(1)}%
            </span>
          </Box>
        </TableCell>
        <TableCell align="center">
          {renderComplianceStatusChip(complianceTier, t, classes)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className={classes.expandRow}>
          <TableCell colSpan={7} className={classes.detailsSection}>
            <DirectReportDetails
              directReport={directReport}
              classes={classes}
              t={t}
              accessLevel={accessLevel}
              selectedClient={selectedClient}
              getTranslation={getTranslation}
              preferredLanguage={preferredLanguage}
              onOpenAsTeamRoot={onOpenAsTeamRoot}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Nested team hierarchy under a direct report (modules / teams / members).
const DirectReportTeamHierarchy = ({ directReport, classes, t = (value) => value }) => {
  const teams = directReport?.teams ?? [];
  const outstandingModules = useMemo(
    () => getOutstandingModulesForDirectReport(directReport),
    [directReport]
  );
  const complianceTier = getComplianceTier(outstandingModules.length);
  const isOutOfCompliance = complianceTier.level !== 'ok';

  return (
    <Box>
      {isOutOfCompliance && (
        <Box mb={3}>
          <Typography variant="h6" style={{ marginBottom: 8, fontWeight: 600, color: '#B71C1C' }}>
            {t('Modules to Complete')}
          </Typography>
          {outstandingModules.length === 0 ? (
            <Alert severity="info">
              {t('No incomplete module details were returned for this direct report.')}
            </Alert>
          ) : (
            <TableContainer className={classes.tableContainer} style={{ marginBottom: 8 }}>
              <Table size="small">
                <TableHead>
                  <TableRow className={classes.tableHeader}>
                    <TableCell style={{ color: 'white' }}>{t('Module')}</TableCell>
                    <TableCell style={{ color: 'white' }}>{t('Member')}</TableCell>
                    <TableCell style={{ color: 'white' }} align="center">{t('Status')}</TableCell>
                    <TableCell style={{ color: 'white' }} align="center">{t('Due Date')}</TableCell>
                    <TableCell style={{ color: 'white' }} align="center">{t('Completion')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstandingModules.map((module) => (
                    <TableRow key={module.key} hover>
                      <TableCell style={{ fontWeight: 600 }}>
                        {module.trainingId ? (
                          <MuiLink
                            component={RouterLink}
                            to={`/Dashboard/Training?pad=&id=${encodeURIComponent(module.trainingId)}`}
                            style={{ color: '#0055A2', fontWeight: 700 }}
                          >
                            {module.moduleName}
                          </MuiLink>
                        ) : (
                          module.moduleName
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {module.memberName}
                        </Typography>
                        {module.memberEmail ? (
                          <Typography variant="caption" color="textSecondary">
                            {module.memberEmail}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={module.status}
                          size="small"
                          style={getModuleStatusChipStyle(module.status)}
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell align="center">{formatTeamReportDate(module.dueDate)}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <LinearProgress
                            variant="determinate"
                            value={module.completionPercentage}
                            style={{ width: 80, marginRight: 8 }}
                          />
                          <span style={{ minWidth: 36, fontWeight: 600 }}>
                            {module.completionPercentage}%
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600 }}>
        {t('Teams under')} {directReport?.reportName ?? t('Unknown')}
      </Typography>
      <Grid container spacing={2}>
        {teams.map((team) => {
          const teamMetrics = team?.metrics ?? EMPTY_METRICS;
          return (
            <Grid item xs={12} sm={6} md={6} key={team.teamId}>
              <Card className={classes.teamCard}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {team.teamName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {team.teamDescription}
                  </Typography>
                  <Box mt={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        <strong>{team.userCount ?? 0}</strong> {t('Members')}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#0055A2', fontWeight: 600 }}>
                        {(teamMetrics.completionRate ?? 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={teamMetrics.completionRate ?? 0}
                      style={{ marginBottom: 12 }}
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption">
                          ✓ {t('Completed')}: <strong>{teamMetrics.totalCompleted}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">
                          ⟳ {t('In Progress')}: <strong>{teamMetrics.inProgress}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">
                          ○ {t('Not Started')}: <strong>{teamMetrics.notStarted}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">
                          ⛔ {t('Overdue')}: <strong>{teamMetrics.overdue}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {teams.some((team) => (team?.members?.length ?? 0) > 0) && (
        <Box mt={3}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600 }}>
            {t('Team Members Progress')}
          </Typography>
          {teams.map((team) =>
            (team?.members?.length ?? 0) > 0 ? (
              <Box key={team.teamId} mb={2}>
                <Typography variant="body2" style={{ fontWeight: 600, color: '#0055A2', marginBottom: 8 }}>
                  {team.teamName}
                </Typography>
                <TableContainer style={{ marginBottom: 16 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ background: '#f5f5f5' }}>
                        <TableCell>{t('Member')}</TableCell>
                        <TableCell>{t('Email')}</TableCell>
                        <TableCell align="center">{t('Completion %')}</TableCell>
                        <TableCell align="center">{t('Assigned')}</TableCell>
                        <TableCell align="center">{t('Done')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(team?.members ?? []).map((member) => (
                        <TableRow key={member.userId}>
                          <TableCell>{member.userName}</TableCell>
                          <TableCell>{member.userEmail}</TableCell>
                          <TableCell align="center">
                            <Box display="flex" alignItems="center" justifyContent="center">
                              <LinearProgress
                                variant="determinate"
                                value={member?.overallCompletionRate ?? 0}
                                style={{ width: 80, marginRight: 8 }}
                              />
                              <span style={{ minWidth: 40, fontWeight: 600 }}>
                                {member?.overallCompletionRate ?? 0}%
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {(member?.assignedTraining ?? []).length}
                          </TableCell>
                          <TableCell align="center">
                            {(member?.assignedTraining ?? []).filter(t => t.status === 'Completed').length}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null
          )}
        </Box>
      )}
    </Box>
  );
};

// Expand panel for a direct report. If they manage a team, default to their
// individual training report with an opt-in CTA for team hierarchy details.
const DirectReportDetails = ({
  directReport,
  classes,
  t = (value) => value,
  accessLevel,
  selectedClient,
  getTranslation,
  preferredLanguage,
  onOpenAsTeamRoot,
}) => {
  const { getAccessTokenSilently } = usePortalAuth();
  const selectedClientId = selectedClient?.clientId ?? selectedClient;
  const hasTeam = directReportHasTeam(directReport);
  const targetUserKey = useMemo(
    () => resolveDirectReportTargetUser(directReport),
    [directReport]
  );
  const displayName = directReport?.reportName || targetUserKey || t('User');

  const handleSeeTeamReport = () => {
    // Elevate this direct report to the page-level Team Report and extend breadcrumbs.
    if (typeof onOpenAsTeamRoot === 'function' && targetUserKey) {
      onOpenAsTeamRoot(targetUserKey, displayName);
      return;
    }
  };

  const {
    data: individualApiData,
    isLoading: isIndividualLoading,
    isFetching: isIndividualFetching,
    isError: isIndividualError,
    error: individualError,
  } = useQuery(
    ['team-training-report-target-user', targetUserKey, selectedClientId],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiDataWithTargetUserAndClientId(
        basePCUrl,
        token,
        'getTeamTrainingReport',
        accessLevel,
        targetUserKey,
        selectedClientId
      );
    },
    {
      enabled: hasTeam && !!targetUserKey && !!selectedClientId,
      staleTime: 1000 * 60 * 15,
      cacheTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
      retry: 1,
    }
  );

  const individualReport =
    individualApiData?.trainingReport ?? individualApiData ?? null;
  const individualCurrentUser = individualReport?.currentUser ?? null;
  const teamComplianceTier = getComplianceTier(
    getOutstandingModuleCount(directReport)
  );

  // Leaf reports (no team): keep existing hierarchy/outstanding modules UI.
  if (!hasTeam) {
    return (
      <Box py={3}>
        <DirectReportTeamHierarchy
          directReport={directReport}
          classes={classes}
          t={t}
        />
      </Box>
    );
  }

  return (
    <Box py={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        mb={2}
        style={{ gap: 12 }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#0055A2' }}>
          {displayName}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSeeTeamReport}
            disabled={!targetUserKey}
          >
            {t(`See ${displayName}'s Team Report`)}
          </Button>
          {teamComplianceTier.level !== 'ok' && (
            <Chip
              size="small"
              label={`${t(teamComplianceTier.label)} — ${teamComplianceTier.outstandingCount} ${t('modules outstanding')}`}
              className={classes.statusChip}
              style={{
                background: teamComplianceTier.color,
                color: 'white',
              }}
            />
          )}
        </Box>
      </Box>

      {(isIndividualLoading || isIndividualFetching) && !individualCurrentUser ? (
        <LoadingSpinner className="LinkTableSpinner" box="LinkTableSpinnerBox" />
      ) : individualCurrentUser ? (
        <MyTrainingReport
          currentUser={individualCurrentUser}
          accessLevel={accessLevel}
          selectedClient={selectedClient}
          ownerName={displayName}
          getTranslation={getTranslation}
          preferredLanguage={preferredLanguage}
          embedded
        />
      ) : (
        <Box>
          <Alert severity={isIndividualError ? 'warning' : 'info'} style={{ marginBottom: 16 }}>
            {isIndividualError
              ? `${t('Could not load individual report')}: ${individualError?.message || t('Unknown error')}. ${t('Showing team details instead.')}`
              : t('Personal training modules were not returned for this user. Showing their team report.')}
          </Alert>
          <DirectReportTeamHierarchy
            directReport={directReport}
            classes={classes}
            t={t}
          />
        </Box>
      )}
    </Box>
  );
};

// Main Component
const TeamTrainingReport = (props) => {
  const classes = useStyles();
  const { getAccessTokenSilently } = usePortalAuth();
  const preferredLanguage = props.preferredLanguage;
  const getTranslation = props.getTranslation || ((value) => value);
  const t = (value) => getTranslation(value, preferredLanguage);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedReportEmail, setSelectedReportEmail] = useState(null);
  const [selectedPortalUser, setSelectedPortalUser] = useState(null);
  // Drill/search trail for Team Report breadcrumbs. Empty when no user selected.
  // Search always replaces; "See {name}'s Team Report" always appends.
  const [navTrail, setNavTrail] = useState([]);

  // 'team' | 'user' — top-level toggle when a searched user has both team + personal data.
  const [viewMode, setViewMode] = useState('team');

  // Display-friendly version of the selected username (capitalize first letter).
  const userDisplayName = useMemo(() => {
    const trailLabel = navTrail.length
      ? navTrail[navTrail.length - 1]?.label
      : null;
    if (trailLabel) return trailLabel;
    const raw = selectedPortalUser;
    if (!raw || typeof raw !== 'string') return null;
    return formatTrailLabel(raw);
  }, [selectedPortalUser, navTrail]);

  // The usernameList from get_portal_data is an array of username strings,
  // e.g. ['terry.mccorkle', 'kyle', 'lisa', ...].

  const portalUserOptions = useMemo(() => {
    const list = props.usernameList;
    if (!Array.isArray(list)) return [];
    return list.filter((name) => typeof name === 'string' && name.length > 0);
  }, [props.usernameList]);
  // Build { firstLetter, name } options sorted alphabetically and grouped by first
  // letter (matches the Account Management page Autocomplete style).
  const groupedPortalUserOptions = useMemo(() => {
    return portalUserOptions
      .map((name) => {
        const firstLetter = name[0].toUpperCase();
        return {
          firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
          name,
        };
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
  }, [portalUserOptions]);


  const isAdmin =
    props.accessLevel === 'CompanyAdmin' || props.accessLevel === 'AccountAdmin';

  const selectedClientId = props.selectedClient?.clientId ?? props.selectedClient;

  const { isLoading, data: apiData, isError, error } = useQuery(
    ['team-training-report', selectedClientId],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiData(basePCUrl, token, 'getTeamTrainingReport', props.accessLevel, selectedClientId);
    },

    {
      enabled: !!selectedClientId,
      staleTime: 1000 * 60 * 15, // 15 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
    }
  );

  const {
    data: selectedUserApiData,
    isLoading: isSelectedUserLoading,
    isFetching: isSelectedUserFetching,
    isError: isSelectedUserError,
    error: selectedUserError,
  } = useQuery(
    ['team-training-report-target-user', selectedPortalUser, selectedClientId],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiDataWithTargetUserAndClientId(
        basePCUrl,
        token,
        'getTeamTrainingReport',
        props.accessLevel,
        selectedPortalUser,
        selectedClientId
      );
    },
    {
      enabled: isAdmin && !!selectedPortalUser && !!selectedClientId,
      staleTime: 1000 * 60 * 15, // 15 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
      retry: 1,
    }
  );

  // Reset expand/email filters when the active portal user changes.
  // Defaults to team canvas for the newly selected manager context.
  useEffect(() => {
    setExpandedRow(null);
    setSelectedReportEmail(null);
    setViewMode('team');
  }, [selectedPortalUser]);

  // New client context → drop selection and breadcrumb trail.
  useEffect(() => {
    setNavTrail([]);
    setSelectedPortalUser(null);
    setExpandedRow(null);
    setSelectedReportEmail(null);
    setViewMode('team');
  }, [selectedClientId]);

  const activateTeamContext = (username) => {
    setExpandedRow(null);
    setSelectedReportEmail(null);
    setViewMode('team');
    setSelectedPortalUser(username);
  };

  // Search select: always start a brand-new breadcrumb trail.
  const startTrail = (username, label) => {
    const next = typeof username === 'string' ? username.trim() : '';
    if (!next) return;
    setNavTrail([{ username: next, label: formatTrailLabel(next, label) }]);
    activateTeamContext(next);
  };

  // Search clear (X): no user selected → no breadcrumbs.
  const clearTrail = () => {
    setNavTrail([]);
    activateTeamContext(null);
  };

  // Direct-report "See {name}'s Team Report": extend the current trail.
  const pushDrill = (username, label) => {
    const next = typeof username === 'string' ? username.trim() : '';
    if (!next) return;
    const crumb = { username: next, label: formatTrailLabel(next, label) };
    setNavTrail((prev) => {
      if (!prev.length) return [crumb];
      const last = prev[prev.length - 1];
      if (last?.username === next) return prev;
      return [...prev, crumb];
    });
    activateTeamContext(next);
  };

  // Breadcrumb click: walk back up the direct-report chain.
  const goToTrailIndex = (index) => {
    if (!navTrail.length || index < 0 || index >= navTrail.length) return;
    const nextTrail = navTrail.slice(0, index + 1);
    const target = nextTrail[nextTrail.length - 1];
    setNavTrail(nextTrail);
    activateTeamContext(target?.username ?? null);
  };

  const handleOpenAsTeamRoot = (username, label) => {
    pushDrill(username, label);
  };

  const reportBreadcrumbs =
    navTrail.length > 0 ? (
      <Box className={classes.breadcrumbBar}>
        <Breadcrumbs
          className={classes.breadcrumbs}
          separator={<NavigateNextIcon className={classes.breadcrumbSeparator} />}
          aria-label={t('Team report navigation')}
        >
          <button
            type="button"
            className={classes.breadcrumbHome}
            onClick={clearTrail}
            aria-label={t('Team Report home')}
            title={t('Team Report home')}
          >
            <HomeIcon fontSize="small" />
          </button>
          {navTrail.map((crumb, index) => {
            const isLastUser = index === navTrail.length - 1;
            const label = crumb.label || crumb.username;
            if (isLastUser) {
              return (
                <Typography
                  key={`${crumb.username}-${index}`}
                  className={classes.breadcrumbUserCurrent}
                  variant="body2"
                  component="span"
                >
                  {label}
                </Typography>
              );
            }
            return (
              <MuiLink
                key={`${crumb.username}-${index}`}
                component="button"
                type="button"
                variant="body2"
                className={classes.breadcrumbLink}
                onClick={() => goToTrailIndex(index)}
              >
                {label}
              </MuiLink>
            );
          })}
          <Typography
            className={classes.breadcrumbPageCurrent}
            variant="body2"
            component="span"
          >
            <AssessmentIcon className={classes.breadcrumbPageIcon} />
            {t('Team Training Compliance Report')}
          </Typography>
        </Breadcrumbs>
      </Box>
    ) : null;

  const selectedUserReport = useMemo(() => {
    if (!selectedPortalUser || !selectedUserApiData) return null;
    return selectedUserApiData?.trainingReport ?? selectedUserApiData;
  }, [selectedPortalUser, selectedUserApiData]);

  const reportData = selectedPortalUser
    ? selectedUserReport
    : (apiData?.trainingReport ?? apiData);


  const manager = reportData?.managers?.[0];
  const managerMetrics = manager?.metrics ?? EMPTY_METRICS;
  const directReports = manager?.directReports ?? [];
  const summary = reportData?.summary ?? EMPTY_SUMMARY;

  // If the selected user has only currentUser data (no managers), render My Report.
  const selectedUserCurrentUser = reportData?.currentUser;
  const showMyReportFallback =
    !!selectedPortalUser &&
    !manager &&
    !!selectedUserCurrentUser;
  const hasBothViews =
    !!selectedPortalUser &&
    !!manager &&
    !!selectedUserCurrentUser;
  const effectiveViewMode = hasBothViews ? viewMode : 'team';

  const reportControls = (
    <Box className={classes.controls}>
      {isAdmin && (
        <Autocomplete
          options={groupedPortalUserOptions}
          groupBy={(option) => option.firstLetter}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option?.name ?? ''
          }
          getOptionSelected={(o, v) =>
            o.name === (typeof v === 'string' ? v : v?.name)
          }
          filterOptions={(opts, state) => {
            const q = (state.inputValue || '').toLowerCase();
            if (!q) return opts;
            return opts.filter((o) =>
              (o?.name || '').toLowerCase().startsWith(q)
            );
          }}
          noOptionsText={t('No matching users')}
          value={
            groupedPortalUserOptions.find((o) => o.name === selectedPortalUser) ||
            (selectedPortalUser
              ? {
                  firstLetter: /[0-9]/.test((selectedPortalUser[0] || '').toUpperCase())
                    ? '0-9'
                    : (selectedPortalUser[0] || '').toUpperCase(),
                  name: selectedPortalUser,
                }
              : null)
          }
          onChange={(_, option) => {
            const name = option?.name ?? null;
            if (name) startTrail(name);
            else clearTrail();
          }}
          style={{ minWidth: 260 }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('Search user')}
              variant="outlined"
              size="small"
            />
          )}
        />
      )}
      {hasBothViews && (
        <ToggleButtonGroup
          value={effectiveViewMode}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) setViewMode(val); }}
        >
          <ToggleButton value="team">{t('Team Report')}</ToggleButton>
          <ToggleButton value="user">
            {userDisplayName ? `${userDisplayName}'s ${t('Report')}` : t('My Report')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>
  );

  // Calculate KPIs using outstanding-module compliance tiers.
  const kpis = useMemo(() => {
    if (!manager) {
      return { completion: 0, inProgress: 0, atRisk: 0, needsWork: 0, overdue: 0 };
    }
    const metrics = manager?.metrics ?? EMPTY_METRICS;
    const reports = manager?.directReports ?? [];
    const tiers = reports.map((report) => getComplianceTier(getOutstandingModuleCount(report)));
    return {
      completion: metrics.completionRate ?? 0,
      inProgress: metrics.inProgress ?? 0,
      atRisk: tiers.filter((tier) => tier.level === 'at_risk').length,
      needsWork: tiers.filter((tier) => tier.level === 'needs_work').length,
      overdue: metrics.overdue ?? 0,
    };
  }, [manager]);

  // Reports needing manager attention: Needs Work + At Risk
  const attentionReports = useMemo(() => {
    if (!manager) return [];
    return (manager?.directReports ?? [])
      .map((report) => {
        const outstandingCount = getOutstandingModuleCount(report);
        return {
          report,
          outstandingCount,
          tier: getComplianceTier(outstandingCount),
        };
      })
      .filter((entry) => entry.tier.level !== 'ok')
      .sort((a, b) => {
        const rank = (level) => (level === 'at_risk' ? 0 : 1);
        const byLevel = rank(a.tier.level) - rank(b.tier.level);
        if (byLevel !== 0) return byLevel;
        return b.outstandingCount - a.outstandingCount;
      });
  }, [manager]);

  const atRiskReports = useMemo(
    () => attentionReports.filter((entry) => entry.tier.level === 'at_risk').map((entry) => entry.report),
    [attentionReports]
  );

  // Email search options for the admin's own direct reports
  const reportEmailOptions = useMemo(
    () =>
      directReports
        .filter(r => r?.reportEmail)
        .map(r => ({
          email: r.reportEmail,
          name: r.reportName,
          id: r.reportId,
        })),
    [directReports]
  );

  // Filtered list that drives the Direct Reports Performance table
  const visibleReports = useMemo(() => {
    if (!selectedReportEmail) return directReports;
    const target = selectedReportEmail.toLowerCase();
    return directReports.filter(r => (r?.reportEmail ?? '').toLowerCase() === target);
  }, [directReports, selectedReportEmail]);

  // Auto-expand the matched row when a single report is selected
  useEffect(() => {
    if (selectedReportEmail && visibleReports.length === 1) {
      setExpandedRow(visibleReports[0].reportId);
    } else if (!selectedReportEmail) {
      setExpandedRow(null);
    }
  }, [selectedReportEmail, visibleReports]);

  // Clear the email filter if the underlying manager/data changes and the
  // previously selected email is no longer a direct report.
  useEffect(() => {
    if (
      selectedReportEmail &&
      !reportEmailOptions.some(o => o.email.toLowerCase() === selectedReportEmail.toLowerCase())
    ) {
      setSelectedReportEmail(null);
    }
  }, [reportEmailOptions, selectedReportEmail]);

  if (
    isLoading ||
    (selectedPortalUser && !selectedUserReport && (isSelectedUserLoading || isSelectedUserFetching))
  ) {
    return <LoadingSpinner className="LinkTableSpinner" box="LinkTableSpinnerBox" />;
  }
  const hasSelectedUserFetchError =
    !!selectedPortalUser && !selectedUserReport && isSelectedUserError;
  if (isError || hasSelectedUserFetchError) {
    return (
      <div className={classes.root}>
        <Alert severity="error">
          {t('Failed to load report')}: {(hasSelectedUserFetchError ? selectedUserError : error)?.message || t('Unknown error')}
        </Alert>
      </div>
    );
  }
  if (effectiveViewMode === 'user' && selectedUserCurrentUser) {
    return (
      <div className={classes.root}>
        <Box className={classes.header}>
          {reportBreadcrumbs}
          <Typography variant="h3" className={classes.title}>
            {t('Team Training Compliance Report')}
          </Typography>
          {reportControls}
        </Box>
        <MyTrainingReport
          currentUser={selectedUserCurrentUser}
          accessLevel={props.accessLevel}
          selectedClient={props.selectedClient}
          ownerName={userDisplayName || selectedPortalUser}
          getTranslation={getTranslation}
          preferredLanguage={preferredLanguage}
        />
      </div>
    );
  }

  if (!manager) {
    return (
      <div className={classes.root}>
        <Box className={classes.header}>
          {reportBreadcrumbs}
          <Typography variant="h3" className={classes.title}>
            {t('Team Training Compliance Report')}
          </Typography>
          {reportControls}
        </Box>
        {showMyReportFallback ? (
          <MyTrainingReport
            currentUser={selectedUserCurrentUser}
            accessLevel={props.accessLevel}
            selectedClient={props.selectedClient}
            ownerName={userDisplayName || selectedPortalUser}
            getTranslation={getTranslation}
            preferredLanguage={preferredLanguage}
          />
        ) : (
          <Typography>{t('No report data available.')}</Typography>
        )}
      </div>
    );
  }


  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        {reportBreadcrumbs}
        <Typography variant="h3" className={classes.title}>
          {t('Team Training Compliance Report')}
        </Typography>
        {reportControls}
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} className={classes.kpiGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t('Overall Completion Rate')}
            value={`${kpis.completion.toFixed(1)}%`}
            subtitle={`${managerMetrics.totalCompleted} ${t('of')} ${managerMetrics.totalAssigned} ${t('modules')}`}
            trend={t('+2.5% from last period')}
            colorClass={classes.successColor}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t('In Progress')}
            value={kpis.inProgress}
            subtitle={t('modules across all teams')}
            trend={t('No change')}
            colorClass={classes.infoColor}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t('Needs Work (Direct Reports)')}
            value={kpis.needsWork}
            subtitle={t('1-3 modules outstanding')}
            colorClass={classes.warningColor}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t('At Risk (Direct Reports)')}
            value={kpis.atRisk}
            subtitle={t('4+ modules outstanding')}
            colorClass={classes.dangerColor}
            classes={classes}
          />
        </Grid>
      </Grid>

      {/* Risk Alert */}
      {attentionReports.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 24 }}>
          <strong>
            ⚠️ {t('Attention Required')} ({attentionReports.length} {t('Direct Reports')})
          </strong>
          <Box mt={1}>
            {attentionReports.map(({ report, outstandingCount, tier }) => (
              <Typography key={report.reportId} variant="body2">
                • {report.reportName} — {t(tier.label)} ({outstandingCount}{' '}
                {t('modules outstanding')}, {(report?.metrics?.completionRate ?? 0).toFixed(1)}%{' '}
                {t('completion')})
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      {/* Direct Reports Section */}
      <Box className={classes.section}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          mb={2}
          style={{ gap: 16 }}
        >
          <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            {t('Direct Reports Performance')}
          </Typography>
          {isAdmin && reportEmailOptions.length > 0 && (
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Autocomplete
                options={reportEmailOptions}
                getOptionLabel={(o) => (o ? `${o.name} — ${o.email}` : '')}
                getOptionSelected={(o, v) => o.email === v.email}
                value={
                  reportEmailOptions.find(
                    o => o.email.toLowerCase() === (selectedReportEmail || '').toLowerCase()
                  ) || null
                }
                onChange={(_, option) => setSelectedReportEmail(option?.email ?? null)}
                filterOptions={(opts, state) => {
                  const q = (state.inputValue || '').toLowerCase();
                  if (!q) return opts;
                  return opts.filter(
                    o =>
                      o.email.toLowerCase().includes(q) ||
                      (o.name || '').toLowerCase().includes(q)
                  );
                }}
                style={{ minWidth: 320 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Search a direct report by email')}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
              {selectedReportEmail && (
                <Chip
                  label={`${t('Showing')} 1 ${t('of')} ${directReports.length}`}
                  onDelete={() => setSelectedReportEmail(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>
        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell style={{ color: 'white', width: 40 }}></TableCell>
                <TableCell style={{ color: 'white' }}>{t('Team Lead')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Email')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Teams')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Members')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Completion %')}</TableCell>
                <TableCell style={{ color: 'white' }} align="center">{t('Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {selectedReportEmail
                        ? t('No direct report matches that email.')
                        : t('No direct reports on record.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleReports.map((directReport) => (
                  <DirectReportRow
                    key={directReport.reportId}
                    directReport={directReport}
                    classes={classes}
                    expanded={expandedRow === directReport.reportId}
                    onExpand={setExpandedRow}
                    t={t}
                    accessLevel={props.accessLevel}
                    selectedClient={props.selectedClient}
                    getTranslation={getTranslation}
                    preferredLanguage={preferredLanguage}
                    onOpenAsTeamRoot={handleOpenAsTeamRoot}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Summary Stats */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {t('Overall Summary')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.kpiCard}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {t('Total Teams')}
                </Typography>
                <Typography variant="h5" style={{ color: '#0055A2', fontWeight: 700 }}>
                  {summary.totalTeams}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.kpiCard}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {t('Total Users')}
                </Typography>
                <Typography variant="h5" style={{ color: '#0055A2', fontWeight: 700 }}>
                  {summary.totalUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.kpiCard}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {t('Compliance Status')}
                </Typography>
                <Typography variant="h5" style={{ color: '#4CAF50', fontWeight: 700 }}>
                  {summary.complianceStatus}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.kpiCard}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>
                  {t('Risk Level')}
                </Typography>
                <Typography variant="h5" style={{ color: '#4CAF50', fontWeight: 700 }}>
                  {summary.overallRiskLevel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default TeamTrainingReport;
